/**
 * /saas/subscriptions/$subId — Subscription detail page.
 *
 * No GET-by-ID endpoint exists in the API, so the subscription is
 * found from the all-subscriptions list (which is already cached by
 * the parent /saas page). Plans and customers are also resolved from
 * their cached lists.
 *
 * API surface used:
 *   GET  /api/saas/subscriptions                    → Subscription[] (find by id)
 *   GET  /api/saas/plans                            → Plan[]
 *   GET  /api/saas/recognition/schedules            → RecognitionSchedule[]
 *   POST /api/saas/subscriptions/{id}/cancel        → SubscriptionOut
 *   POST /api/saas/subscriptions/{id}/invoice       → { ar_invoice_id, total }
 *   GET  /api/saas/subscriptions/{id}/usage         → UsageEvent[]
 *   POST /api/saas/subscriptions/{id}/usage         → UsageEvent
 *   GET  /api/saas/subscriptions/{id}/dunning       → DunningAttempt[]
 *   POST /api/saas/subscriptions/{id}/dunning/open  → DunningAttempt
 *   POST /api/saas/subscriptions/{id}/dunning/clear → 204
 */
import { createFileRoute, Link, useParams } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Chip } from "@mui/material";

import { api } from "@/lib/api";
import {
  partiesListQueryOptions,
  saasKeys,
  saasPlansQueryOptions,
  saasRecognitionSchedulesQueryOptions,
  saasSubscriptionsQueryOptions,
  saasDunningQueryOptions,
  saasUsageEventsQueryOptions,
} from "@/lib/queries";
import { useConfirmAction } from "@/components/ConfirmDialog";
import { useToast } from "@/components/Toaster";
import type {
  DunningAttempt,
  Plan,
  RecognitionSchedule,
  Subscription,
  SubscriptionStatus,
  UsageEvent,
} from "@/lib/types";

export const Route = createFileRoute("/_auth/saas/subscriptions/$subId")({
  component: SubscriptionDetailPage,
});

const SUB_STATUS_COLOR: Record<
  SubscriptionStatus,
  "success" | "warning" | "error" | "default"
> = {
  trialing: "warning",
  active: "success",
  past_due: "error",
  paused: "warning",
  canceled: "default",
  expired: "default",
};

function fmtMoney(value: string, currency = "USD"): string {
  return Number(value).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }) + " " + currency;
}

function DetailRow({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) {
  return (
    <div className="flex items-start gap-4 py-2.5 border-b border-slate-100 dark:border-gray-800">
      <span className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400 w-40 shrink-0 pt-0.5">
        {label}
      </span>
      <span className="text-sm text-slate-800 dark:text-slate-200">{value}</span>
    </div>
  );
}

function SubscriptionDetailPage() {
  const { subId } = useParams({ from: "/_auth/saas/subscriptions/$subId" });
  const qc = useQueryClient();
  const toast = useToast();
  const confirmAction = useConfirmAction();

  const [cancelImmediate, setCancelImmediate] = useState(false);
  const [usageQty, setUsageQty] = useState("");
  const [usagePlanId, setUsagePlanId] = useState("");
  const [usageDesc, setUsageDesc] = useState("");
  const [showUsageForm, setShowUsageForm] = useState(false);

  const subsQuery = useQuery(saasSubscriptionsQueryOptions());
  const plansQuery = useQuery(saasPlansQueryOptions());
  const customersQuery = useQuery(
    partiesListQueryOptions({ party_type: "customer", active_only: false }),
  );
  const schedulesQuery = useQuery(saasRecognitionSchedulesQueryOptions());
  const usageQuery = useQuery(saasUsageEventsQueryOptions(subId));
  const dunningQuery = useQuery(saasDunningQueryOptions(subId));

  const sub: Subscription | undefined = useMemo(
    () => (subsQuery.data ?? []).find((s) => s.id === subId),
    [subsQuery.data, subId],
  );

  const plansById = useMemo(() => {
    const m: Record<string, Plan> = {};
    (plansQuery.data ?? []).forEach((p) => { m[p.id] = p; });
    return m;
  }, [plansQuery.data]);

  const customer = useMemo(
    () => (customersQuery.data ?? []).find((c) => c.id === sub?.customer_id),
    [customersQuery.data, sub],
  );

  // Recognition schedules linked to this subscription
  const subSchedules = useMemo<RecognitionSchedule[]>(
    () =>
      (schedulesQuery.data ?? []).filter((s) => s.subscription_id === subId),
    [schedulesQuery.data, subId],
  );

  const cancelSub = useMutation({
    mutationFn: async (immediate: boolean) =>
      api<Subscription>(`/api/saas/subscriptions/${subId}/cancel`, {
        method: "POST",
        body: { immediate, cancel_date: null },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: saasKeys.all });
      toast.success("Subscription canceled");
    },
    onError: (err: Error) => toast.error(`Cancel failed: ${err.message}`),
  });

  const invoiceNow = useMutation({
    mutationFn: async () =>
      api<{ ar_invoice_id: string; total: string }>(
        `/api/saas/subscriptions/${subId}/invoice`,
        { method: "POST" },
      ),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: saasKeys.all });
      toast.success(`Invoice created — total ${r.total}`);
    },
    onError: (err: Error) => toast.error(`Invoice failed: ${err.message}`),
  });

  const recordUsage = useMutation({
    mutationFn: async () =>
      api<UsageEvent>(`/api/saas/subscriptions/${subId}/usage`, {
        method: "POST",
        body: {
          plan_id: usagePlanId,
          quantity: usageQty,
          description: usageDesc || null,
        },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [...saasKeys.all, "usage", subId] });
      toast.success("Usage recorded");
      setUsageQty("");
      setUsageDesc("");
      setShowUsageForm(false);
    },
    onError: (err: Error) => toast.error(`Usage failed: ${err.message}`),
  });

  const openDunning = useMutation({
    mutationFn: async () =>
      api<DunningAttempt>(`/api/saas/subscriptions/${subId}/dunning/open`, {
        method: "POST",
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [...saasKeys.all, "dunning", subId] });
      qc.invalidateQueries({ queryKey: saasKeys.all });
      toast.success("Dunning opened — subscription marked past-due");
    },
    onError: (err: Error) => toast.error(`Failed: ${err.message}`),
  });

  const clearDunning = useMutation({
    mutationFn: async () =>
      api(`/api/saas/subscriptions/${subId}/dunning/clear`, { method: "POST" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [...saasKeys.all, "dunning", subId] });
      qc.invalidateQueries({ queryKey: saasKeys.all });
      toast.success("Dunning cleared — subscription reactivated");
    },
    onError: (err: Error) => toast.error(`Failed: ${err.message}`),
  });

  const setupBilling = useMutation({
    mutationFn: async () =>
      api<{ checkout_url: string; stripe_customer_id: string }>(
        `/api/saas/subscriptions/${subId}/billing/checkout`,
        { method: "POST" },
      ),
    onSuccess: (r) => {
      window.open(r.checkout_url, "_blank", "noopener,noreferrer");
      toast.success("Stripe Checkout opened — complete payment in the new tab");
    },
    onError: (err: Error) => toast.error(`Billing setup failed: ${err.message}`),
  });

  const manageBilling = useMutation({
    mutationFn: async () =>
      api<{ portal_url: string }>(
        `/api/saas/subscriptions/${subId}/billing/portal`,
        { method: "POST" },
      ),
    onSuccess: (r) => {
      window.open(r.portal_url, "_blank", "noopener,noreferrer");
    },
    onError: (err: Error) => toast.error(`Could not open billing portal: ${err.message}`),
  });

  const requestCancel = () => {
    if (!sub) return;
    confirmAction({
      title: `Cancel ${sub.subscription_no}?`,
      message: cancelImmediate
        ? `IMMEDIATE cancellation: the subscription ends today. No further invoices will be generated.`
        : `Cancel at end of current period (${sub.current_period_end}). The subscription remains active until then.`,
      confirmLabel: cancelImmediate ? "Cancel immediately" : "Cancel at period end",
      kind: "danger",
      onConfirm: async () => {
        await cancelSub.mutateAsync(cancelImmediate);
      },
    });
  };

  const isLoading = subsQuery.isLoading || plansQuery.isLoading || customersQuery.isLoading;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-40 text-slate-500 dark:text-slate-400 text-sm">
        Loading…
      </div>
    );
  }

  if (!sub) {
    return (
      <div className="px-6 py-8 text-center">
        <div className="text-slate-700 dark:text-slate-200 font-semibold mb-2">
          Subscription not found
        </div>
        <div className="text-sm text-slate-500 dark:text-slate-400 mb-4">
          ID: <code className="font-mono text-xs">{subId}</code>
        </div>
        <Link
          to="/saas"
          className="text-sm text-blue-600 hover:underline"
        >
          Back to SaaS
        </Link>
      </div>
    );
  }

  const isActive = sub.status === "active" || sub.status === "trialing";
  const planItems = sub.items.map((it) => plansById[it.plan_id]);

  return (
    <div className="flex flex-col h-[calc(100vh-56px)] overflow-hidden">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 px-6 py-3 dark:bg-gray-900 dark:border-gray-800">
        <div className="flex items-center gap-3">
          <Link
            to="/saas"
            className="text-sm text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
          >
            SaaS
          </Link>
          <span className="text-slate-400 dark:text-slate-600">/</span>
          <h1 className="text-xl font-semibold text-slate-900 dark:text-slate-100">
            {sub.subscription_no}
          </h1>
          <Chip
            label={sub.status}
            size="small"
            color={SUB_STATUS_COLOR[sub.status]}
            sx={{ fontSize: 10, height: 20 }}
          />
          {sub.cancel_at_period_end && sub.status !== "canceled" && (
            <Chip
              label="cancels at period end"
              size="small"
              color="warning"
              variant="outlined"
              sx={{ fontSize: 10, height: 20 }}
            />
          )}
        </div>
      </div>

      <div className="flex-1 overflow-auto px-6 py-6">
        <div className="max-w-3xl mx-auto flex flex-col gap-6">
          {/* Actions */}
          {isActive && (
            <div className="flex items-center gap-3 flex-wrap">
              <button
                onClick={() => invoiceNow.mutate()}
                disabled={invoiceNow.isPending}
                className="text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-md px-3 py-1.5 font-medium disabled:opacity-50"
                title="Generate AR invoice for the current period now"
              >
                {invoiceNow.isPending ? "Invoicing…" : "Invoice now"}
              </button>

              {!sub.external_ref ? (
                <button
                  onClick={() => setupBilling.mutate()}
                  disabled={setupBilling.isPending}
                  className="text-sm bg-violet-600 hover:bg-violet-700 text-white rounded-md px-3 py-1.5 font-medium disabled:opacity-50"
                  title="Connect this subscription to Stripe so the customer can pay by card"
                >
                  {setupBilling.isPending ? "Opening…" : "💳 Set up billing"}
                </button>
              ) : (
                <button
                  onClick={() => manageBilling.mutate()}
                  disabled={manageBilling.isPending}
                  className="text-sm bg-white hover:bg-slate-50 text-slate-700 border border-slate-300 rounded-md px-3 py-1.5 font-medium disabled:opacity-50 dark:bg-gray-800 dark:text-slate-200 dark:border-gray-600 dark:hover:bg-gray-700"
                  title="Open Stripe Customer Portal — update card, view invoices, cancel"
                >
                  {manageBilling.isPending ? "Opening…" : "💳 Manage billing"}
                </button>
              )}

              <div className="flex items-center gap-2 ml-auto">
                <label className="flex items-center gap-1.5 text-xs text-slate-600 dark:text-slate-400 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={cancelImmediate}
                    onChange={(e) => setCancelImmediate(e.target.checked)}
                    className="rounded"
                  />
                  Immediate (ends today)
                </label>
                <button
                  onClick={requestCancel}
                  disabled={cancelSub.isPending}
                  className="text-sm bg-white hover:bg-red-50 text-red-600 border border-red-300 rounded-md px-3 py-1.5 font-medium disabled:opacity-50"
                >
                  {cancelSub.isPending ? "Canceling…" : "Cancel subscription"}
                </button>
              </div>
            </div>
          )}

          {/* Core details */}
          <div className="bg-white border border-slate-200 rounded-lg overflow-hidden dark:bg-gray-900 dark:border-gray-800">
            <div className="bg-slate-50 px-4 py-2 text-xs uppercase tracking-wide text-slate-600 font-semibold border-b border-slate-200 dark:bg-gray-950 dark:border-gray-800 dark:text-slate-300">
              Subscription details
            </div>
            <div className="px-4">
              <DetailRow label="Subscription #" value={sub.subscription_no} />
              <DetailRow
                label="Customer"
                value={customer?.name ?? (
                  <span className="font-mono text-xs text-slate-500 dark:text-slate-400">
                    {sub.customer_id}
                  </span>
                )}
              />
              <DetailRow
                label="Status"
                value={
                  <Chip
                    label={sub.status}
                    size="small"
                    color={SUB_STATUS_COLOR[sub.status]}
                    sx={{ fontSize: 10, height: 20 }}
                  />
                }
              />
              <DetailRow
                label="Started"
                value={sub.started_at}
              />
              {sub.trial_end && (
                <DetailRow label="Trial ends" value={sub.trial_end} />
              )}
              <DetailRow
                label="Current period"
                value={`${sub.current_period_start} → ${sub.current_period_end}`}
              />
              <DetailRow
                label="Next invoice"
                value={sub.next_invoice_date ?? "—"}
              />
              {sub.canceled_at && (
                <DetailRow label="Canceled at" value={sub.canceled_at} />
              )}
              <DetailRow
                label="Currency"
                value={sub.currency_code}
              />
              <DetailRow
                label="Stripe"
                value={
                  sub.external_ref ? (
                    <span className="font-mono text-xs text-slate-600 dark:text-slate-400">
                      {sub.external_ref}
                    </span>
                  ) : (
                    <span className="text-xs text-slate-400 dark:text-slate-500 italic">
                      Not linked — click "Set up billing"
                    </span>
                  )
                }
              />
              {Number(sub.discount_percent) > 0 && (
                <DetailRow
                  label="Discount"
                  value={
                    <span className="text-amber-700 font-semibold">
                      {sub.discount_percent}%
                    </span>
                  }
                />
              )}
            </div>
          </div>

          {/* Plan items */}
          <div className="bg-white border border-slate-200 rounded-lg overflow-hidden dark:bg-gray-900 dark:border-gray-800">
            <div className="bg-slate-50 px-4 py-2 text-xs uppercase tracking-wide text-slate-600 font-semibold border-b border-slate-200 dark:bg-gray-950 dark:border-gray-800 dark:text-slate-300">
              Plan items ({sub.items.length})
            </div>
            {sub.items.length === 0 ? (
              <div className="px-4 py-6 text-sm text-slate-500 dark:text-slate-400">
                No items on this subscription.
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead className="text-xs uppercase tracking-wide text-slate-600 border-b border-slate-100 bg-white dark:bg-gray-900 dark:border-gray-800 dark:text-slate-300">
                  <tr>
                    <th className="text-left px-4 py-2">Plan</th>
                    <th className="text-right px-4 py-2">Price</th>
                    <th className="text-right px-4 py-2">Qty</th>
                    <th className="text-left px-4 py-2">Started</th>
                    <th className="text-left px-4 py-2">Canceled</th>
                  </tr>
                </thead>
                <tbody>
                  {sub.items.map((item) => {
                    const plan = plansById[item.plan_id];
                    const price = item.unit_amount_override ?? plan?.amount ?? "—";
                    const currency = plan?.currency_code ?? sub.currency_code;
                    const intervalLabel =
                      plan
                        ? plan.interval_count === 1
                          ? `/ ${plan.interval}`
                          : `/ ${plan.interval_count} ${plan.interval}s`
                        : "";
                    return (
                      <tr
                        key={item.id}
                        className="border-t border-slate-100 dark:border-gray-800"
                      >
                        <td className="px-4 py-2">
                          <div className="font-medium text-slate-800 dark:text-slate-200">
                            {plan?.name ?? (
                              <span className="font-mono text-xs text-slate-500 dark:text-slate-400">
                                {item.plan_id}
                              </span>
                            )}
                          </div>
                          {plan?.code && (
                            <div className="text-[10px] text-slate-500 dark:text-slate-400">
                              {plan.code}
                              {item.unit_amount_override && (
                                <span className="ml-1 text-amber-600">
                                  (price overridden)
                                </span>
                              )}
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-2 text-right font-mono text-sm">
                          {price !== "—" ? fmtMoney(price as string, currency) : "—"}
                          <span className="text-xs text-slate-500 dark:text-slate-400 ml-1">
                            {intervalLabel}
                          </span>
                        </td>
                        <td className="px-4 py-2 text-right">
                          {item.quantity}
                        </td>
                        <td className="px-4 py-2 text-xs text-slate-600 dark:text-slate-400">
                          {item.started_at}
                        </td>
                        <td className="px-4 py-2 text-xs text-slate-500 dark:text-slate-400">
                          {item.canceled_at ?? "—"}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>

          {/* Billing summary (computed from items) */}
          {sub.items.length > 0 && (
            <div className="bg-slate-50 border border-slate-200 rounded-lg px-4 py-3 dark:bg-gray-950 dark:border-gray-800">
              <div className="text-xs text-slate-500 dark:text-slate-400 mb-1 uppercase tracking-wide font-semibold">
                Billing summary
              </div>
              <div className="text-sm text-slate-700 dark:text-slate-300">
                {sub.items.map((item) => {
                  const plan = plansById[item.plan_id];
                  const unit = Number(item.unit_amount_override ?? plan?.amount ?? 0);
                  const subtotal = unit * item.quantity;
                  const discount = Number(sub.discount_percent) / 100;
                  const discounted = subtotal * (1 - discount);
                  return (
                    <div key={item.id} className="flex justify-between py-0.5">
                      <span>
                        {plan?.name ?? item.plan_id}
                        {item.quantity > 1 && (
                          <span className="text-slate-500 dark:text-slate-400">
                            {" "}× {item.quantity}
                          </span>
                        )}
                      </span>
                      <span className="font-mono">
                        {discounted.toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}{" "}
                        {sub.currency_code}
                        {discount > 0 && (
                          <span className="text-amber-600 text-xs ml-1">
                            (after {sub.discount_percent}% disc.)
                          </span>
                        )}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Recognition schedules */}
          {subSchedules.length > 0 && (
            <div className="bg-white border border-slate-200 rounded-lg overflow-hidden dark:bg-gray-900 dark:border-gray-800">
              <div className="bg-slate-50 px-4 py-2 text-xs uppercase tracking-wide text-slate-600 font-semibold border-b border-slate-200 dark:bg-gray-950 dark:border-gray-800 dark:text-slate-300">
                Recognition schedules ({subSchedules.length})
              </div>
              <table className="w-full text-sm">
                <thead className="text-xs uppercase tracking-wide text-slate-600 border-b border-slate-100 bg-white dark:bg-gray-900 dark:border-gray-800 dark:text-slate-300">
                  <tr>
                    <th className="text-left px-4 py-2">Service window</th>
                    <th className="text-right px-4 py-2">Total</th>
                    <th className="text-right px-4 py-2">Recognized</th>
                    <th className="text-right px-4 py-2">Remaining</th>
                    <th className="text-left px-4 py-2">Progress</th>
                  </tr>
                </thead>
                <tbody>
                  {subSchedules.map((s: RecognitionSchedule) => {
                    const total = Number(s.total_amount);
                    const posted = s.entries
                      .filter((e) => e.status === "posted")
                      .reduce((sum, e) => sum + Number(e.amount), 0);
                    const remaining = total - posted;
                    const pct = total > 0 ? (posted / total) * 100 : 0;
                    const postedCount = s.entries.filter((e) => e.status === "posted").length;
                    const fmt = (n: number) =>
                      n.toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      });
                    return (
                      <tr
                        key={s.id}
                        className="border-t border-slate-100 dark:border-gray-800"
                      >
                        <td className="px-4 py-2 text-xs">
                          <div className="text-slate-700 dark:text-slate-300">
                            {s.service_start} → {s.service_end}
                          </div>
                          <div className="text-[10px] text-slate-500 dark:text-slate-400">
                            {s.period_count} periods · {s.currency_code}
                          </div>
                        </td>
                        <td className="px-4 py-2 text-right font-mono">
                          ${fmt(total)}
                        </td>
                        <td className="px-4 py-2 text-right font-mono text-emerald-700">
                          ${fmt(posted)}
                        </td>
                        <td className="px-4 py-2 text-right font-mono text-amber-700">
                          ${fmt(remaining)}
                        </td>
                        <td className="px-4 py-2">
                          <div className="flex items-center gap-2">
                            <div className="flex-1 bg-slate-200 h-2 rounded overflow-hidden min-w-[80px] dark:bg-gray-700">
                              <div
                                className="bg-emerald-500 h-full"
                                style={{ width: `${pct}%` }}
                              />
                            </div>
                            <span className="text-[10px] text-slate-600 font-mono w-12 text-right dark:text-slate-300">
                              {postedCount}/{s.period_count}
                            </span>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Recognition placeholder when none exist */}
          {subSchedules.length === 0 && !schedulesQuery.isLoading && (
            <div className="bg-white border border-slate-200 rounded-lg px-4 py-5 dark:bg-gray-900 dark:border-gray-800">
              <div className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400 font-semibold mb-1">
                Recognition schedules
              </div>
              <div className="text-sm text-slate-500 dark:text-slate-400">
                No recognition schedules for this subscription.
                <br />
                <span className="text-xs">
                  Schedules are created automatically when an invoice posts.
                </span>
              </div>
            </div>
          )}

          {/* ── Metered usage ──────────────────────────────────── */}
          {planItems.some((p) => p?.is_metered) && (
            <div className="bg-white border border-slate-200 rounded-lg overflow-hidden dark:bg-gray-900 dark:border-gray-800">
              <div className="bg-slate-50 px-4 py-2 text-xs uppercase tracking-wide text-slate-600 font-semibold border-b border-slate-200 dark:bg-gray-950 dark:border-gray-800 dark:text-slate-300 flex items-center justify-between">
                <span>Metered usage</span>
                <button
                  onClick={() => {
                    const firstMetered = sub.items.find(
                      (it) => plansById[it.plan_id]?.is_metered,
                    );
                    if (firstMetered) setUsagePlanId(firstMetered.plan_id);
                    setShowUsageForm((v) => !v);
                  }}
                  className="text-xs text-blue-600 hover:underline font-normal"
                >
                  {showUsageForm ? "Cancel" : "+ Record usage"}
                </button>
              </div>

              {showUsageForm && (
                <div className="px-4 py-3 bg-blue-50 border-b border-blue-100 dark:bg-blue-950/30 dark:border-blue-900">
                  <div className="flex items-end gap-3 flex-wrap">
                    <div className="flex flex-col gap-1">
                      <label className="text-[10px] uppercase tracking-wide text-slate-500 dark:text-slate-400">
                        Plan
                      </label>
                      <select
                        value={usagePlanId}
                        onChange={(e) => setUsagePlanId(e.target.value)}
                        className="text-sm border border-slate-200 rounded px-2 py-1 bg-white dark:bg-gray-900 dark:border-gray-700"
                      >
                        {sub.items
                          .filter((it) => plansById[it.plan_id]?.is_metered)
                          .map((it) => (
                            <option key={it.plan_id} value={it.plan_id}>
                              {plansById[it.plan_id]?.name ?? it.plan_id}
                            </option>
                          ))}
                      </select>
                    </div>
                    <div className="flex flex-col gap-1">
                      <label className="text-[10px] uppercase tracking-wide text-slate-500 dark:text-slate-400">
                        Quantity
                      </label>
                      <input
                        type="number"
                        min="0.0001"
                        step="any"
                        value={usageQty}
                        onChange={(e) => setUsageQty(e.target.value)}
                        placeholder="e.g. 100"
                        className="text-sm border border-slate-200 rounded px-2 py-1 w-28 dark:bg-gray-900 dark:border-gray-700"
                      />
                    </div>
                    <div className="flex flex-col gap-1 flex-1 min-w-[140px]">
                      <label className="text-[10px] uppercase tracking-wide text-slate-500 dark:text-slate-400">
                        Description (optional)
                      </label>
                      <input
                        type="text"
                        value={usageDesc}
                        onChange={(e) => setUsageDesc(e.target.value)}
                        placeholder="e.g. API calls batch #4"
                        className="text-sm border border-slate-200 rounded px-2 py-1 dark:bg-gray-900 dark:border-gray-700"
                      />
                    </div>
                    <button
                      onClick={() => recordUsage.mutate()}
                      disabled={recordUsage.isPending || !usageQty || !usagePlanId}
                      className="text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-md px-3 py-1.5 font-medium disabled:opacity-50"
                    >
                      {recordUsage.isPending ? "Saving…" : "Save"}
                    </button>
                  </div>
                </div>
              )}

              {usageQuery.isLoading ? (
                <div className="px-4 py-4 text-sm text-slate-500 dark:text-slate-400">Loading…</div>
              ) : (usageQuery.data ?? []).length === 0 ? (
                <div className="px-4 py-4 text-sm text-slate-500 dark:text-slate-400">
                  No usage events recorded yet.
                </div>
              ) : (
                <table className="w-full text-sm">
                  <thead className="text-xs uppercase tracking-wide text-slate-600 border-b border-slate-100 bg-white dark:bg-gray-900 dark:border-gray-800 dark:text-slate-300">
                    <tr>
                      <th className="text-left px-4 py-2">Plan</th>
                      <th className="text-left px-4 py-2">Occurred at</th>
                      <th className="text-right px-4 py-2">Qty</th>
                      <th className="text-left px-4 py-2">Description</th>
                      <th className="text-center px-4 py-2">Billed</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(usageQuery.data ?? []).map((ev) => (
                      <tr key={ev.id} className="border-t border-slate-100 dark:border-gray-800">
                        <td className="px-4 py-2 text-xs text-slate-600 dark:text-slate-400">
                          {plansById[ev.plan_id]?.name ?? ev.plan_id}
                        </td>
                        <td className="px-4 py-2 text-xs font-mono text-slate-600 dark:text-slate-400">
                          {new Date(ev.occurred_at).toLocaleString()}
                        </td>
                        <td className="px-4 py-2 text-right font-mono">
                          {Number(ev.quantity).toLocaleString()}
                        </td>
                        <td className="px-4 py-2 text-xs text-slate-500 dark:text-slate-400">
                          {ev.description ?? "—"}
                        </td>
                        <td className="px-4 py-2 text-center">
                          {ev.invoiced ? (
                            <span className="text-[10px] text-emerald-600 font-semibold uppercase">✓ billed</span>
                          ) : (
                            <span className="text-[10px] text-amber-600 uppercase">pending</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}

              {/* Uninvoiced total */}
              {(usageQuery.data ?? []).some((ev) => !ev.invoiced) && (() => {
                const uninvoicedByPlan: Record<string, number> = {};
                (usageQuery.data ?? [])
                  .filter((ev) => !ev.invoiced)
                  .forEach((ev) => {
                    uninvoicedByPlan[ev.plan_id] = (uninvoicedByPlan[ev.plan_id] ?? 0) + Number(ev.quantity);
                  });
                return (
                  <div className="px-4 py-2 border-t border-slate-100 dark:border-gray-800 bg-amber-50 dark:bg-amber-950/20">
                    <div className="text-xs text-amber-700 dark:text-amber-400 font-medium">
                      Unbilled usage:{" "}
                      {Object.entries(uninvoicedByPlan).map(([planId, qty]) => (
                        <span key={planId} className="mr-3">
                          {plansById[planId]?.name ?? planId}:{" "}
                          <strong>{qty.toLocaleString()}</strong>{" "}
                          {plansById[planId]?.unit_label ?? "units"}
                        </span>
                      ))}
                    </div>
                  </div>
                );
              })()}
            </div>
          )}

          {/* ── Dunning history ────────────────────────────────── */}
          {(sub.status === "past_due" || (dunningQuery.data ?? []).length > 0) && (
            <div className="bg-white border border-red-200 rounded-lg overflow-hidden dark:bg-gray-900 dark:border-red-900">
              <div className="bg-red-50 px-4 py-2 text-xs uppercase tracking-wide text-red-600 font-semibold border-b border-red-200 dark:bg-red-950/30 dark:border-red-900 dark:text-red-400 flex items-center justify-between">
                <span>⚠ Dunning / past-due</span>
                <div className="flex items-center gap-2">
                  {sub.status !== "past_due" && (
                    <button
                      onClick={() => openDunning.mutate()}
                      disabled={openDunning.isPending}
                      className="text-xs text-red-600 hover:underline font-normal disabled:opacity-50"
                    >
                      {openDunning.isPending ? "Opening…" : "Open dunning"}
                    </button>
                  )}
                  {sub.status === "past_due" && (
                    <button
                      onClick={() => clearDunning.mutate()}
                      disabled={clearDunning.isPending}
                      className="text-xs bg-emerald-600 hover:bg-emerald-700 text-white rounded px-2 py-0.5 font-medium disabled:opacity-50"
                    >
                      {clearDunning.isPending ? "Clearing…" : "Mark payment received"}
                    </button>
                  )}
                </div>
              </div>

              {dunningQuery.isLoading ? (
                <div className="px-4 py-4 text-sm text-slate-500 dark:text-slate-400">Loading…</div>
              ) : (dunningQuery.data ?? []).length === 0 ? (
                <div className="px-4 py-4 text-sm text-slate-500 dark:text-slate-400">
                  No dunning attempts recorded.
                </div>
              ) : (
                <table className="w-full text-sm">
                  <thead className="text-xs uppercase tracking-wide text-slate-600 border-b border-slate-100 bg-white dark:bg-gray-900 dark:border-gray-800 dark:text-slate-300">
                    <tr>
                      <th className="text-center px-4 py-2">#</th>
                      <th className="text-left px-4 py-2">Attempted</th>
                      <th className="text-left px-4 py-2">Next attempt</th>
                      <th className="text-center px-4 py-2">Status</th>
                      <th className="text-left px-4 py-2">Reason</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(dunningQuery.data ?? []).map((d) => {
                      const statusColor: Record<string, string> = {
                        pending: "text-amber-600",
                        succeeded: "text-emerald-600",
                        failed: "text-red-600",
                        abandoned: "text-slate-500",
                      };
                      return (
                        <tr key={d.id} className="border-t border-slate-100 dark:border-gray-800">
                          <td className="px-4 py-2 text-center font-semibold">{d.attempt_no}</td>
                          <td className="px-4 py-2 text-xs font-mono text-slate-600 dark:text-slate-400">
                            {new Date(d.attempted_at).toLocaleDateString()}
                          </td>
                          <td className="px-4 py-2 text-xs text-slate-600 dark:text-slate-400">
                            {d.next_attempt_at ?? "—"}
                          </td>
                          <td className={`px-4 py-2 text-center text-xs font-semibold uppercase ${statusColor[d.status] ?? ""}`}>
                            {d.status}
                          </td>
                          <td className="px-4 py-2 text-xs text-slate-500 dark:text-slate-400">
                            {d.failure_reason ?? "—"}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
